// UIFIX3 and UIFIX4 (SF-17, design/style-guide.md section 8): the one row-action pattern for every table. A row with more
// than one action shows a single "Actions" menu; a row with one action keeps its plain button. The buttons are moved, not
// rebuilt, so each keeps its handler, test id, label and permission gate. Touch only: a tap opens it, a tap outside or
// Escape closes it, and there is no hover content.

export interface RowMenuOptions {
  /** Names the row for the trigger's accessible label ("Actions for Review Owner"). */
  subject: string;
  buttons: HTMLButtonElement[];
  /** The trigger's test id. Defaults to `row-menu-toggle`. */
  testId?: string;
}

export function createRowMenu(options: RowMenuOptions): HTMLElement {
  const { buttons } = options;
  const wrap = document.createElement("div");
  wrap.className = "row-menu";
  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "btn-secondary btn-compact row-menu-trigger";
  trigger.dataset["testid"] = options.testId ?? "row-menu-toggle";
  trigger.setAttribute("aria-haspopup", "menu");
  trigger.setAttribute("aria-expanded", "false");
  trigger.setAttribute("aria-label", `Actions for ${options.subject}`);
  trigger.textContent = "Actions ▾";
  const panel = document.createElement("div");
  panel.className = "row-menu-panel";
  panel.setAttribute("role", "menu");
  panel.hidden = true;
  const close = (): void => {
    panel.hidden = true;
    trigger.setAttribute("aria-expanded", "false");
    document.removeEventListener("pointerdown", outside, true);
  };
  function outside(event: Event): void {
    if (!wrap.contains(event.target as Node)) close();
  }
  for (const button of buttons) {
    button.classList.add("row-menu-item");
    button.setAttribute("role", "menuitem");
    button.addEventListener("click", () => close());
    panel.append(button);
  }
  trigger.addEventListener("click", () => {
    if (!panel.hidden) {
      close();
      return;
    }
    panel.hidden = false;
    trigger.setAttribute("aria-expanded", "true");
    document.addEventListener("pointerdown", outside, true);
    buttons[0]?.focus();
  });
  wrap.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !panel.hidden) {
      close();
      trigger.focus();
      event.stopPropagation();
    }
  });
  wrap.append(trigger, panel);
  return wrap;
}

/** Moves every button in `actions` into one Actions menu when there is more than one. */
export function collapseRowActions(
  actions: HTMLElement,
  options: { subject: string; testId?: string },
): HTMLElement | null {
  const buttons = Array.from(actions.children).filter(
    (child): child is HTMLButtonElement => child instanceof HTMLButtonElement,
  );
  if (buttons.length <= 1) return null;
  const menu = createRowMenu({ ...options, buttons });
  actions.append(menu);
  return menu;
}
